import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, Zap, Eye, Battery, Leaf } from "lucide-react";
import AnimatedShort from "@/components/AnimatedShort";

const benefits = [
  { icon: Eye, title: "Comfort visivo", desc: "Riduce l'affaticamento degli occhi, specialmente in ambienti con poca luce" },
  { icon: Battery, title: "Risparmio energetico", desc: "Fino al 60% in meno di consumo batteria su schermi OLED e AMOLED" },
  { icon: Zap, title: "Focus massimo", desc: "I contenuti emergono dallo sfondo scuro, guidando l'attenzione dove conta" },
  { icon: Leaf, title: "Sostenibile", desc: "Meno energia consumata = meno emissioni. Il dark mode e' anche una scelta green" },
];

export default function DarkIntroPage() {
  const navigate = useNavigate();
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const enterSite = (theme) => {
    localStorage.setItem("sb-theme", theme);
    if (theme === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0c1220] flex items-center justify-center px-4 py-12 overflow-hidden">
      <div className={`max-w-2xl w-full transition-all duration-1000 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

        {/* Moon Icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1e293b] to-[#0c1220] flex items-center justify-center border border-white/10" style={{ boxShadow: '0 0 60px rgba(66,133,244,0.15), 0 0 120px rgba(66,133,244,0.05)' }}>
              <Moon className="w-10 h-10 text-white/80" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#4285F4] animate-pulse"></div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily:"'Poppins',sans-serif" }}>
            Dark by Design
          </h1>
          <p className="text-gray-400 text-base leading-relaxed max-w-lg mx-auto">
            <span style={{ WebkitTextStroke:'1px #000', paintOrder:'stroke fill' }}>
              <span className="text-[#4285F4]">sch</span><span className="text-[#EA4335]">ed</span><span className="text-[#FBBC04]">abu</span><span className="text-[#4285F4]">sin</span><span className="text-[#34A853]">ess</span><span className="text-[#EA4335]">.com</span>
            </span>
            {" "}e' progettato in dark mode.
            <br />Non e' un'opzione: e' una scelta consapevole.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 flex gap-3 items-start" style={{ animationDelay: `${i * 150}ms` }}>
              <div className="w-9 h-9 rounded-lg bg-[#4285F4]/10 flex items-center justify-center flex-shrink-0">
                <b.icon className="w-4 h-4 text-[#4285F4]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">{b.title}</p>
                <p className="text-gray-500 text-xs leading-relaxed mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Animated Short — storytelling */}
        <div className="mb-10">
          <AnimatedShort compact />
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => enterSite("dark")}
            className="bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full px-10 py-3.5 font-black text-base transition-all hover:scale-105 inline-flex items-center gap-2"
            style={{ boxShadow: '0 4px 20px rgba(66,133,244,0.3)' }}
            data-testid="enter-dark"
          >
            <Moon className="w-5 h-5" /> Entra nel dark side
          </button>
          <button
            onClick={() => enterSite("light")}
            className="text-gray-500 hover:text-gray-300 text-sm font-medium transition-colors inline-flex items-center gap-1.5 py-2"
            data-testid="enter-light"
          >
            <Sun className="w-4 h-4" /> Preferisco la versione chiara
          </button>
        </div>
      </div>
    </div>
  );
}
